<template>
  <!-- Story CD-5 -->
  <div v-if="isLoading" class="flex justify-center items-center min-h-[300px]">
    <LayupSpinner />
  </div>
  <div class="min-h-screen bg-white flex flex-col items-center" :class="{ 'mb-6': isMobile }" v-else>
    <SuccessErrorModal
      :show-modal="showSuccess"
      :title="modalData.title"
      :description="modalData.description"
      :isError="modalData.isError"
      @close="onClose"
    />

    <div class="w-full max-w-[640px] px-6 pt-2">
      <h1 class="text-[30px] font-semibold text-center text-gray-light-900">Contact Us</h1>
    </div>

    <p v-if="error" class="text-error-600 text-[14px]">{{ error }}</p>

    <!-- CONTACT US FORM -->
    <div class="w-full max-w-[512px] px-2 pt-[30px]">
      <div class="flex flex-col gap-6">
        <TextInput
          id="contactFirstName"
          label="First Name"
          v-model:input="formValues.firstName"
          :disabled="isLoading"
          test-id="contact_form_name"
          placeholder="Enter first name"
        />

        <TextInput
          id="contactSurname"
          label="Surname"
          v-model:input="formValues.surname"
          :disabled="isLoading"
          test-id="contact_form_surname"
          placeholder="Enter surname"
        />

        <TextInput
          id="contactPhone"
          label="Phone Number"
          v-model:input="formValues.contactNumber"
          :disabled="isLoading"
          test-id="contact_form_phone_number"
          placeholder="Enter phone number"
        />

        <TextInput
          id="contactEmail"
          label="Email Address"
          v-model:input="formValues.email"
          :regex="emailRegex"
          :disabled="isLoading"
          test-id="contact_form_email"
          placeholder="Enter email address"
        />

        <CustomSelect
          label="Subject"
          placeholder="Select Subject"
          :items="subjects"
          v-model="formValues.subject"
          test-id="contact_form_subject"
        />

        <TextBoxInput
          label="Message"
          placeholder="Type your message here"
          v-model="formValues.message"
          test-id="contact_form_message"
        />

        <div v-if="formValues.subject === 'Submit a support ticket'">
          <UploadFiles ref="supportDocs" @uploaded="onFilesUploaded" @uploading="onUploading" />
        </div>
      </div>

      <div class="flex justify-center pt-10">
        <Button
          class="px-10"
          :class="isDesktop ? 'w-[132px]' : 'w-full'"
          :disabled="!fieldsValid || isLoading || uploadingFiles"
          @click="onSubmit"
        >
          Send Message
        </Button>
      </div>
    </div>

    <!-- CONTACT DETAILS INFO -->
    <div class="w-full flex justify-between items-center px-6 py-10 text-sm text-primary-600">
      <div class="flex items-center gap-2 text-brand-600 text-[14px]">
        <PhoneIcon class="w-4 h-4" />
        <span>+27 87 094 5555</span>
      </div>

      <div class="flex items-center gap-2 text-brand-600 text-[14px]">
        <MailIcon class="w-4 h-4" />
        <span>support@layup.co.za</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CustomSelect from '@/components/common/CustomSelect.vue';
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SuccessErrorModal from '@/components/common/SuccessErrorModal.vue';
import TextBoxInput from '@/components/common/TextBoxInput.vue';
import TextInput from '@/components/common/TextInput.vue';
import UploadFiles from '@/components/common/UploadFiles.vue';
import { Button } from '@/components/ui/button';
import { NetworkError } from '@/errors';
import { sendEmail } from '@/services/emails';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { MailIcon, PhoneIcon } from 'lucide-vue-next';
import { computed, reactive, ref } from 'vue';

const { isMobile, isDesktop } = useBreakpoint();
const userStore = useUserStore();
const { user } = userStore;

const isLoading = ref(false);
const error = ref('');
const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const subjects = ref(['General Customer Service', 'Suggestions', 'Product Support', 'Submit a support ticket']);
const showSuccess = ref(false);
const modalData = ref({
  title: '',
  description: '',
  isError: false,
});
const uploadingFiles = ref(false);

const populateForm = () => {
  formValues.firstName = user.name ?? '';
  formValues.email = user.email ?? '';
  formValues.contactNumber = user.cellNumber ?? '';
};

const formValues = reactive({
  firstName: '',
  surname: '',
  contactNumber: '',
  email: '',
  subject: '',
  message: '',
  type: 'contact',
  attachment: [] as string[],
});

const fieldsValid = computed(() => {
  return (
    formValues.firstName &&
    formValues.surname &&
    formValues.contactNumber &&
    emailRegex.test(formValues.email) &&
    formValues.subject &&
    formValues.message &&
    formValues.type
  );
});

const onSubmit = async () => {
  error.value = '';
  if (!fieldsValid.value) {
    error.value = 'Please fill in all fields before submitting.';
    return;
  }

  try {
    isLoading.value = true;
    const type = formValues.subject === 'Submit a support ticket' ? 'support' : 'contact';
    const name = `${formValues.firstName} ${formValues.surname}`;

    await sendEmail({ ...formValues, type, name });

    showSuccess.value = true;
    modalData.value = {
      title: "We'll Be In Touch Soon!",
      description:
        "Thanks for reaching out. Our support team is reviewing your message and will get back to you within 24 hours. Keep an eye on your inbox - we'll be in touch soon!",
      isError: false,
    };

    // reset form
    Object.assign(formValues, {
      firstName: user.name ?? '',
      surname: '',
      contactNumber: user.cellNumber ?? '',
      email: user.email ?? '',
      subject: '',
      message: '',
      type: 'contact',
      attachment: [],
    });
  } catch (error) {
    showSuccess.value = true;
    modalData.value = {
      title: 'Something went wrong',
      description: 'Failed sending message. Please try again',
      isError: true,
    };

    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric(`contact_us_modal`, { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric(`contact_us_modal`, { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onFilesUploaded = (urls: string[]) => {
  formValues.attachment = urls;
};

const onUploading = (isUploading: boolean) => {
  uploadingFiles.value = isUploading;
};

const onClose = () => {
  showSuccess.value = false;
};

const init = () => {
  populateForm();
};

init();
</script>
